// utils/exportCommon.ts
import {
  SimulationResult,
  UniversalForm,
  StockForm,
  PropertyForm,
  BondForm
} from './simulationTypes';

export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

export const formatPercentage = (value: number): string => {
  return `${value.toFixed(2)}%`;
};

// Type guard functions
const isStockForm = (form: UniversalForm): form is StockForm => {
  return 'annualIncomeAmount' in form && 'brokerage' in form;
};

const isPropertyForm = (form: UniversalForm): form is PropertyForm => {
  return 'monthlyIncomeAmount' in form && 'loanAmount' in form;
};

const isBondForm = (form: UniversalForm): form is BondForm => {
  return 'fees' in form && 'setupCosts' in form && 'interestRateMin' in form;
};

export const exportToPDF = async (
  result: SimulationResult | null,
  reportRef: React.RefObject<HTMLDivElement>,
  assetName: string,
  setExportMenuAnchor: (anchor: HTMLElement | null) => void
) => {
  if (!result || !reportRef.current) return;

  try {
    const { jsPDF } = await import('jspdf');
    const html2canvas = await import('html2canvas');

    const canvas = await html2canvas.default(reportRef.current, {
      scale: 2,
      useCORS: true,
      allowTaint: true
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    pdf.save(`${assetName}_Investment_Report.pdf`);
  } catch (error) {
    console.error('PDF export failed:', error);
  }
  setExportMenuAnchor(null);
};

export const exportToExcel = (
  result: SimulationResult | null,
  form: UniversalForm,
  setExportMenuAnchor: (anchor: HTMLElement | null) => void,
  assetType: 'stock' | 'property' | 'bond' = 'stock'
) => {
  if (!result) return;

  const asset = result.individual_assets[0];
  const analysis = asset.investment_performance_analysis;

  // Base data common to all asset types
  let data = [
    [`${assetType.charAt(0).toUpperCase() + assetType.slice(1)} Investment Analysis Report`],
    ['Generated on:', new Date().toLocaleDateString()],
    [''],
    [`${assetType.charAt(0).toUpperCase() + assetType.slice(1)} Details`],
    ['Asset Name', form.assetName],
    ['Purchase Price', `$${form.purchasePrice.toLocaleString()}`],
    ['Investment Period', `${form.years} years`],
    [''],
    ['Key Results'],
    ['Expected Annual Return (P50)', analysis.expected_returns_p50.annual_return],
    ['Total Profit (P50)', analysis.expected_returns_p50.total_profit],
    ['Probability of Profit', analysis.risk_assessment.probability_of_profit],
    ['Best Case Scenario', analysis.risk_assessment.best_case],
    ['Worst Case Scenario', analysis.risk_assessment.worst_case],
    [''],
    ['Investment Parameters']
  ];

  // Add asset-specific parameters
  if (assetType === 'stock' && isStockForm(form)) {
    data.push(['Annual Dividend Income', `$${form.annualIncomeAmount.toLocaleString()}`]);
    data.push([
      'Dividend Growth Range',
      `${(form.incomeGrowthMin * 100).toFixed(1)}% - ${(form.incomeGrowthMax * 100).toFixed(1)}%`
    ]);
    data.push(['Management Fee', `${(form.managementFeePercent * 100).toFixed(2)}%`]);
  } else if (assetType === 'property' && isPropertyForm(form)) {
    data.push(['Monthly Rental Income', `$${form.monthlyIncomeAmount.toLocaleString()}`]);
    data.push(['Loan Amount', `$${form.loanAmount.toLocaleString()}`]);
    data.push([
      'Interest Rate Range',
      `${(form.interestRateMin * 100).toFixed(1)}% - ${(form.interestRateMax * 100).toFixed(1)}%`
    ]);
    data.push(['Vacancy Rate', `${(form.vacancyRate * 100).toFixed(1)}%`]);
  } else if (assetType === 'bond' && isBondForm(form)) {
    data.push(['Bond Fees', `$${form.fees.toLocaleString()}`]);
    data.push(['Setup Costs', `$${form.setupCosts.toLocaleString()}`]);
    data.push([
      'Interest Rate Range',
      `${(form.interestRateMin * 100).toFixed(1)}% - ${(form.interestRateMax * 100).toFixed(1)}%`
    ]);
    data.push(['Management Fee', `${(form.managementFeePercent * 100).toFixed(2)}%`]);
  }

  // Add common market parameters
  if ('capitalGrowthMin' in form && 'capitalGrowthMax' in form) {
    data.push([
      'Capital Growth Range',
      `${(form.capitalGrowthMin * 100).toFixed(1)}% - ${(form.capitalGrowthMax * 100).toFixed(1)}%`
    ]);
  } else if ('capitalGrowthFixed' in form) {
    data.push(['Capital Growth (Fixed)', `${(form.capitalGrowthFixed * 100).toFixed(1)}%`]);
  }

  if ('volatilityStdDev' in form) {
    data.push(['Market Volatility', `${(form.volatilityStdDev * 100).toFixed(1)}%`]);
  }

  const csvContent = data.map((row) => row.join(',')).join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${form.assetName}_${assetType}_Data.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setExportMenuAnchor(null);
};

export const exportToWord = (
  result: SimulationResult | null,
  form: UniversalForm,
  setExportMenuAnchor: (anchor: HTMLElement | null) => void,
  assetType: 'stock' | 'property' | 'bond' = 'stock'
) => {
  if (!result) return;

  const asset = result.individual_assets[0];
  const analysis = asset.investment_performance_analysis;

  // Generate asset-specific details
  let assetSpecificDetails = '';
  if (assetType === 'property' && isPropertyForm(form)) {
    assetSpecificDetails = `
      <div class="metric"><strong>Monthly Rental Income:</strong> $${form.monthlyIncomeAmount.toLocaleString()}</div>
      <div class="metric"><strong>Loan Amount:</strong> $${form.loanAmount.toLocaleString()}</div>
      <div class="metric"><strong>Interest Rate Range:</strong> ${(
        form.interestRateMin * 100
      ).toFixed(1)}% - ${(form.interestRateMax * 100).toFixed(1)}%</div>
    `;
  } else if (assetType === 'stock' && isStockForm(form)) {
    assetSpecificDetails = `
      <div class="metric"><strong>Annual Dividend Income:</strong> $${form.annualIncomeAmount.toLocaleString()}</div>
      <div class="metric"><strong>Management Fee:</strong> ${(
        form.managementFeePercent * 100
      ).toFixed(2)}%</div>
    `;
  } else if (assetType === 'bond' && isBondForm(form)) {
    assetSpecificDetails = `
      <div class="metric"><strong>Bond Fees:</strong> $${form.fees.toLocaleString()}</div>
      <div class="metric"><strong>Setup Costs:</strong> $${form.setupCosts.toLocaleString()}</div>
      <div class="metric"><strong>Interest Rate Range:</strong> ${(
        form.interestRateMin * 100
      ).toFixed(1)}% - ${(form.interestRateMax * 100).toFixed(1)}%</div>
    `;
  }

  const htmlContent = `
    <html>
      <head>
        <title>${
          assetType.charAt(0).toUpperCase() + assetType.slice(1)
        } Investment Analysis Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { color: #2196f3; }
          h2 { color: #1976d2; margin-top: 30px; }
          .summary { background-color: #f5f5f5; padding: 15px; margin: 10px 0; }
          .metric { margin: 10px 0; }
          .section { margin: 20px 0; }
        </style>
      </head>
      <body>
        <h1>${
          assetType.charAt(0).toUpperCase() + assetType.slice(1)
        } Investment Analysis Report</h1>
        <p><strong>Generated on:</strong> ${new Date().toLocaleDateString()}</p>
        
        <div class="summary">
          <h2>Investment Summary</h2>
          <div class="metric"><strong>${
            assetType.charAt(0).toUpperCase() + assetType.slice(1)
          }:</strong> ${form.assetName}</div>
          <div class="metric"><strong>Investment Amount:</strong> $${form.purchasePrice.toLocaleString()}</div>
          <div class="metric"><strong>Investment Period:</strong> ${form.years} years</div>
          ${assetSpecificDetails}
          <div class="metric"><strong>Expected Annual Return:</strong> ${
            analysis.expected_returns_p50.annual_return
          }</div>
          <div class="metric"><strong>Total Expected Profit:</strong> ${
            analysis.expected_returns_p50.total_profit
          }</div>
          <div class="metric"><strong>Success Probability:</strong> ${
            analysis.risk_assessment.probability_of_profit
          }</div>
        </div>

        <div class="section">
          <h2>Risk Assessment</h2>
          <div class="metric"><strong>Best Case:</strong> ${
            analysis.risk_assessment.best_case
          }</div>
          <div class="metric"><strong>Worst Case:</strong> ${
            analysis.risk_assessment.worst_case
          }</div>
          ${
            'volatilityStdDev' in form
              ? `<div class="metric"><strong>Market Volatility:</strong> ${(
                  form.volatilityStdDev * 100
                ).toFixed(1)}%</div>`
              : ''
          }
        </div>

        <div class="section">
          <h2>Key Investment Insight</h2>
          <p>${analysis.key_takeaway}</p>
        </div>

        <div class="section">
          <h2>Strategic Recommendations</h2>
          <h3>Green Lights:</h3>
          <ul>
            ${analysis.actionable_investment_decisions.green_lights
              .map((item: string) => `<li>${item}</li>`)
              .join('')}
          </ul>
          
          ${
            analysis.actionable_investment_decisions.red_flags_to_monitor?.length
              ? `
            <h3>Red Flags to Monitor:</h3>
            <ul>
              ${analysis.actionable_investment_decisions.red_flags_to_monitor
                .map((item: string) => `<li>${item}</li>`)
                .join('')}
            </ul>
          `
              : ''
          }
        </div>
      </body>
    </html>
  `;

  const blob = new Blob([htmlContent], { type: 'application/msword' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${form.assetName}_${assetType}_Report.doc`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setExportMenuAnchor(null);
};
